import { useQuery } from "@tanstack/react-query";
import { signedMediaUrl } from "./media";
import { localMediaUrl } from "./local-media";

/** Signed media URLs expire after 15 minutes (the media signer's default TTL). */
const SIGNED_TTL = 15 * 60 * 1000;
const PHOTO = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

/** How long a media URL may be reused. Photos resolve to session blob URLs from the photo
 * cache, so they never go stale; signed URLs (video, audio, or a photo when the browser's
 * cache storage is unavailable) refresh before they expire and are dropped before they die. */
export function mediaUrlCache(key: string) {
  const durable = /^https?:\/\//.test(key) || PHOTO.test(key);
  return {
    staleTime: (url: string | undefined) => (url?.startsWith("blob:") || /^https?:\/\//.test(key) ? Infinity : SIGNED_TTL - 5 * 60 * 1000),
    gcTime: durable ? 24 * 60 * 60 * 1000 : SIGNED_TTL - 3 * 60 * 1000,
  };
}

/** Every image, video and voice note resolves its URL here, under one cache key, so a photo
 * shown on Home appears instantly in Discuss, the inbox and profiles without reloading. */
export function useMediaUrl(key?: string | null) {
  const cache = mediaUrlCache(key ?? "");
  return useQuery({
    queryKey: ["signed-media", key],
    // Media posted from this device shows from its local copy, before (and while) it uploads.
    queryFn: () => (/^https?:\/\//.test(key!) ? Promise.resolve(key!) : Promise.resolve(localMediaUrl(key!) ?? signedMediaUrl(key!))),
    enabled: !!key,
    staleTime: (query) => cache.staleTime(query.state.data),
    gcTime: cache.gcTime,
  });
}
